import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as GrowthIcon,
  Code as CodeIcon,
  Work as WorkIcon,
  School as LearnIcon,
  EmojiEvents as SuccessIcon,
} from '@mui/icons-material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const MentorshipPage: React.FC = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    document.title = 'Mentorship | Kevyn Franco';
  }, []);

  const benefits = [
    {
      icon: <WorkIcon sx={{ fontSize: 40, color: '#dc143c' }} />,
      title: 'Land Your Dream Job',
      description: 'Get hired at top tech companies with proven strategies for resumes, portfolios, and interviews.',
    },
    {
      icon: <GrowthIcon sx={{ fontSize: 40, color: '#dc143c' }} />,
      title: 'Accelerate Your Career',
      description: 'Switch to better roles with higher compensation and better work-life balance.',
    },
    {
      icon: <CodeIcon sx={{ fontSize: 40, color: '#dc143c' }} />,
      title: 'Master Technical Skills',
      description: 'Deep dive into system design, algorithms, and production-ready code practices.',
    },
    {
      icon: <SuccessIcon sx={{ fontSize: 40, color: '#dc143c' }} />,
      title: 'Ace Technical Interviews',
      description: 'Prepare for coding challenges, system design rounds, and behavioral interviews.',
    },
  ];

  const whatYouGet = [
    '1-on-1 personalized mentorship sessions',
    'Resume and portfolio review with actionable feedback',
    'Mock interviews with real-time feedback',
    'Career roadmap tailored to your goals',
    'Access to exclusive resources and templates',
    'Ongoing support via email between sessions',
    'Salary negotiation strategies',
    'Network introductions when relevant',
  ];

  const successStories = [
    {
      name: 'Software Engineer',
      result: 'Landed job at Google',
      detail: 'From bootcamp grad to FAANG in 6 months',
    },
    {
      name: 'Frontend Developer',
      result: '50% salary increase',
      detail: 'Switched from agency to product company',
    },
    {
      name: 'Backend Engineer',
      result: 'Promoted to Senior',
      detail: 'Mastered system design and leadership',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Header />
      
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: '#1a1a1a',
          color: 'white',
          py: { xs: 8, md: 12 },
          borderBottom: '4px solid #dc143c',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: '"Impact", "Arial Black", sans-serif',
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 3,
                color: '#dc143c',
              }}
            >
              TECH MENTORSHIP
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                mb: 4,
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Get personalized guidance to land your dream job, ace technical interviews, 
              and accelerate your software engineering career.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setContactModalOpen(true)}
              sx={{
                bgcolor: '#dc143c',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderRadius: 0,
                border: '3px solid #dc143c',
                '&:hover': {
                  bgcolor: '#b01030',
                  borderColor: '#b01030',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Start Your Journey →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Impact", "Arial Black", sans-serif',
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            textTransform: 'uppercase',
            textAlign: 'center',
            mb: 6,
            color: '#1a1a1a',
          }}
        >
          How I Can Help You
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {benefits.map((benefit, index) => (
            <Box key={index} sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: 'white',
                  border: '3px solid #dc143c',
                  borderRadius: 0,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, bgcolor: 'white' }}>
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: '"Arial Black", sans-serif',
                      fontWeight: 900,
                      mb: 2,
                      color: '#1a1a1a',
                    }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#333',
                      lineHeight: 1.8,
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>

      {/* What You Get Section */}
      <Box sx={{ bgcolor: '#1a1a1a', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Impact", "Arial Black", sans-serif',
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              textTransform: 'uppercase',
              textAlign: 'center',
              mb: 6,
              color: '#dc143c',
            }}
          >
            What's Included
          </Typography>

          <Paper
            sx={{
              bgcolor: 'white',
              border: '3px solid #dc143c',
              borderRadius: 0,
              p: 4,
            }}
          >
            <List>
              {whatYouGet.map((item, index) => (
                <ListItem key={index} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: '#dc143c', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#1a1a1a',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Container>
      </Box>

      {/* Success Stories */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Impact", "Arial Black", sans-serif',
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            textTransform: 'uppercase',
            textAlign: 'center',
            mb: 6,
            color: '#1a1a1a',
          }}
        >
          Success Stories
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {successStories.map((story, index) => (
            <Box key={index} sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 22px)' } }}>
              <Card
                sx={{
                  bgcolor: '#1a1a1a',
                  color: 'white',
                  border: '3px solid #dc143c',
                  borderRadius: 0,
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <LearnIcon sx={{ fontSize: 50, color: '#dc143c', mb: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: 'white',
                    }}
                  >
                    {story.name}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: '"Arial Black", sans-serif',
                      fontWeight: 900,
                      color: '#dc143c',
                      mb: 2,
                    }}
                  >
                    {story.result}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#999',
                    }}
                  >
                    {story.detail}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: '#dc143c',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Impact", "Arial Black", sans-serif',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 900,
                textTransform: 'uppercase',
                mb: 3,
              }}
            >
              Ready to Level Up?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                lineHeight: 1.6,
                fontSize: '1.2rem',
              }}
            >
              Let's discuss your career goals and create a personalized plan to get you there.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setContactModalOpen(true)}
              sx={{
                bgcolor: '#1a1a1a',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderRadius: 0,
                border: '3px solid #1a1a1a',
                '&:hover': {
                  bgcolor: '#000',
                  borderColor: '#000',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Get Started Now →
            </Button>
          </Box>
        </Container>
      </Box>

      <Footer />

      <ContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        type="mentorship"
      />
    </Box>
  );
};

export default MentorshipPage;